import dayjs from 'dayjs';

export const DASHBOARD_ANALYTICS_DATE_RANGES = [7, 14, 30, 60, 90] as const;

export type DashboardAnalyticsDateRangeDays = (typeof DASHBOARD_ANALYTICS_DATE_RANGES)[number];

export interface AppsOverTimeEntry {
  date: string;
}

export interface NewlySubmittedAppEntry {
  analysisId: string;
}

export interface AppTypeDistributionEntry {
  managementType: 'MANAGED' | 'REQUESTED' | 'UNMANAGED';
  versionCount: number;
}

export interface TopAppVersionEntry {
  applicationId: string;
  versionCount: number;
  appIconUrl?: string;
}

export interface TopAppCategoryEntry {
  category: string;
  uniqueAppsCount: number;
  versionCount: number;
}

export interface VendorRiskAssessmentEntry {
  vendor: string;
  noteCount: number;
  warningCount: number;
  errorCount: number;
}

export interface TopAppRiskEntry {
  riskName: string;
  versionCount: number;
}

export interface MostRiskAppEntry {
  appName: string;
  applicationId: string;
  vendor: string;
  versionCount: number;
  avgCount: number;
}

export interface MostErrorLevelRisksAppVersionEntry {
  appName: string;
  version: string;
  errorCount: number;
  appIconUrl?: string;
  analysisId: string;
}

// SBOM "Risk Exposure by CVSS" bubble (combined platforms): one point per CVE scoring 7.0+.
export interface RiskExposureCvssEntry {
  cveId: string;
  cvssScore: number;
  maxConfidence: number | null;
  versionsCount: number;
  severity: string;
}

// SBOM "App Versions with most Critical CVEs" table row (per platform).
export interface AppVersionCriticalCveEntry {
  appName: string;
  applicationId: string;
  vendor: string;
  version: string;
  analysisId: string;
  criticalCveCount: number;
}

// SBOM "Most Commonly found Critical CVEs" bar (per platform).
export interface CommonCriticalCveEntry {
  cveId: string;
  count: number;
}

// Metrics row returned by GET /eval/analytics/metrics (subset used by dashboard cards).
export interface DashboardAnalyticsMetrics {
  id: string;
  createdAt: string;
  updatedAt?: string;
  orgId: string;
  dateRange: number;
  totalVersionsAll: number;
  totalVersionsAndroid: number;
  totalVersionsIos: number;
  uniqueAppsAll: number;
  uniqueAppsAndroid: number;
  uniqueAppsIos: number;
  appsOverTime?: AppsOverTimeEntry[];
  newlySubmittedApps?: NewlySubmittedAppEntry[];
  appTypeDistribution?: AppTypeDistributionEntry[];
  topAppVersionsAndroid?: TopAppVersionEntry[];
  topAppVersionsIos?: TopAppVersionEntry[];
  topAppCategoriesAndroid?: TopAppCategoryEntry[];
  topAppCategoriesIos?: TopAppCategoryEntry[];
  unpassedErrorLevelRisksOverTime?: Array<{
    date: string;
    total: number;
    android: number;
    ios: number;
  }>;
  vendorRiskAssessmentAndroid?: VendorRiskAssessmentEntry[];
  vendorRiskAssessmentIos?: VendorRiskAssessmentEntry[];
  topAppRisksAndroid?: TopAppRiskEntry[];
  topAppRisksIos?: TopAppRiskEntry[];
  mostRiskAppsAndroid?: MostRiskAppEntry[];
  mostRiskAppsIos?: MostRiskAppEntry[];
  mostErrorLevelRisksAppVersionsAndroid?: MostErrorLevelRisksAppVersionEntry[];
  mostErrorLevelRisksAppVersionsIos?: MostErrorLevelRisksAppVersionEntry[];
  riskExposureByCvss?: RiskExposureCvssEntry[];
  appVersionsMostCriticalCvesAndroid?: AppVersionCriticalCveEntry[];
  appVersionsMostCriticalCvesIos?: AppVersionCriticalCveEntry[];
  mostCommonCriticalCvesAndroid?: CommonCriticalCveEntry[];
  mostCommonCriticalCvesIos?: CommonCriticalCveEntry[];
}

// dateSubmitted >= NOW() - N days used for the same dateRange query param
export function rollingSubmittedDateRange(days: DashboardAnalyticsDateRangeDays): {
  startDate: string;
  endDate: string;
} {
  const endDate = dayjs().format('YYYY-MM-DD');
  const startDate = dayjs().subtract(days, 'day').format('YYYY-MM-DD');
  return { startDate, endDate };
}
